import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPalette,
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import { useTranslation } from "react-i18next";
import { ValidationResult } from "../../types/forms";
import TextInput from "./TextInput";
import ValidationWarning from "./ValidationWarning";
import { colourHelper } from "../../helpers";

interface ColourPickerProps {
  /** The onChange event, for handling state changes */
  onChange(newValue: string): void;
  /** The Id attribute for the input element, to match the Label */
  inputId?: string;
  /** The current value for the input */
  value: string;
  /** The hex colour for the code used in the 'Preview' popup (and contrast checker) */
  fontColour?: string;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  /** Optional border color for the input wrapper */
  borderColor?: string;
  /** Whether the component should take full width */
  fullWidth?: boolean;
  /** Tailwind border color class for the preview button (e.g., 'border-gray-200') */
  previewBorderColor?: string;
  /** Position of the dropdown overlay relative to the input field */
  overlayPosition?: "ABOVE" | "BELOW";
  /** Whether to perform the accessibility contrast check */
  performAccessibilityCheck?: boolean;
}

/** A color picker with a dropdown overlay approach */
const ColourPicker = ({
  onChange,
  inputId = "",
  value = "",
  fontColour = "#FFFFFF",
  showValidationErrors = false,
  validationResult = null,
  borderColor,
  fullWidth = false,
  previewBorderColor = "border-gray-200",
  overlayPosition = "BELOW",
  performAccessibilityCheck = true,
}: ColourPickerProps) => {
  const { t } = useTranslation();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerColour, setPickerColour] = useState(value);
  const [doesColourPassContrastCheck, setDoesColourPassContrastCheck] =
    useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Only perform contrast check if accessibility check is enabled
  useEffect(() => {
    if (!performAccessibilityCheck) return;

    if (colourHelper.doesColourPassContrastCheck(fontColour, pickerColour)) {
      setDoesColourPassContrastCheck(true);
    } else {
      setDoesColourPassContrastCheck(false);
    }
  }, [pickerColour, fontColour, performAccessibilityCheck]);

  useEffect(() => {
    setPickerColour(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        handleCancel();
      }
    };

    if (isPickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isPickerOpen, value]);

  const handleTextChange = (newValue: string) => {
    if (newValue.length === 0) {
      onChange("#");
    } else {
      onChange(newValue);
    }
  };

  const handleTogglePicker = () => {
    setIsPickerOpen(!isPickerOpen);
  };

  const handleApply = () => {
    // Only check contrast if accessibility check is enabled
    if (!performAccessibilityCheck || doesColourPassContrastCheck) {
      onChange(pickerColour);
      setIsPickerOpen(false);
    }
  };

  const handleCancel = () => {
    setPickerColour(value); // Reset to original value
    setIsPickerOpen(false);
  };

  return (
    <div ref={pickerRef} className="relative">
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}

      <div className="w-full flex items-center gap-3">
        {/* Color Preview - clickable */}
        <button
          type="button"
          onClick={handleTogglePicker}
          style={{ backgroundColor: value }}
          className={`w-11 h-11 rounded-lg border-2 ${previewBorderColor} shadow-sm flex-shrink-0 hover:border-gray-300 hover:shadow transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          title="Open color picker"
          aria-label="Open color picker"
        />

        {/* Input and Button Container */}
        <div className="flex-1 relative">
          <TextInput
            onChange={handleTextChange}
            inputId={inputId}
            className="w-full pr-11 font-mono border border-gray-300"
            value={value}
          />
          <button
            type="button"
            onClick={handleTogglePicker}
            className="absolute right-2.5 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Open color picker"
            aria-label="Toggle color picker"
          >
            <FontAwesomeIcon icon={faPalette} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Dropdown Color Picker Overlay */}
      {isPickerOpen && (
        <div
          className={`absolute z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden left-0 min-w-[240px] ${
            overlayPosition === "ABOVE" ? "bottom-full mb-2" : "mt-2"
          }`}
        >
          {/* Color Picker Content */}
          <div className="p-5">
            {/* Color Picker */}
            <div className="mb-4">
              <HexColorPicker color={pickerColour} onChange={setPickerColour} />
            </div>

            {/* Preview and Value */}
            <div className="flex items-center gap-3 mb-3">
              {/* Color Preview Square */}
              <div
                style={{ backgroundColor: pickerColour }}
                className="w-14 h-14 rounded-lg border-2 border-gray-200 shadow-sm flex-shrink-0"
              />
              {/* Value */}
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1 font-medium">
                  Hex Value
                </div>
                <div className="text-base text-gray-900 font-mono font-semibold">
                  {pickerColour.toUpperCase()}
                </div>
              </div>
            </div>

            {/* Accessibility Status */}
            {performAccessibilityCheck && (
              <div
                className={`px-3 py-2.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
                  doesColourPassContrastCheck
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    doesColourPassContrastCheck
                      ? faCheckCircle
                      : faExclamationTriangle
                  }
                  className="w-3.5 h-3.5"
                />
                <span>
                  {doesColourPassContrastCheck
                    ? "Passes accessibility checks"
                    : "Potential accessibility issues"}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-5 pb-4 flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t("Common.Cancel")}
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={
                performAccessibilityCheck && !doesColourPassContrastCheck
              }
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm"
            >
              {t("Common.Confirm")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColourPicker;
